import {
    IEntities,
    InTransitSettingsKeys,
} from '../components/Applications/Intransit/interfaces'
import { IOrgformData } from '../components/Organizations/interfaces'
import { IformData } from '../components/UserManagement/interfaces'
import { defaultLocalization } from '../utils/DateAndTimeUtils'

export const enum USER_SESSION_AUDIT {
    LOGIN = 'LOGIN',
    LOGOUT = 'LOGOUT',
}

export const enum UserPermissions {
    ORG_ADMIN = 'organization:admin',
    USER_ADMIN = 'user:admin',
    USER_USERORGS = 'user:userorgs',
    ENTITY_ANALYST = 'entity:analyst',
    ENTITY_WRITER = 'entity:writer',
    ENTITY_READER = 'entity:reader',
    DEVICE_WRITER = 'device:writer',
    SYSTEM_ADMIN = 'system:admin',
    SYSTEM_READER = 'system:reader',
}

export const USER_ROLES = {
    ADMIN: 'Admin',
    WRITER: 'Writer',
    READER: 'Reader',
    ANALYST: 'Analyst',
    DEVICE_MANAGER: 'Device Manager',
    SYSTEM_ADMIN: 'System Administrator',
    SYSTEM_READER: 'System Reader',
    VENDOR_MANUFACTURER: 'Vendor Manufacturer',
}

/** UBQ-5579: roles only a Superadmin may assign, change, or resend an invitation for. */
export const SUPER_ADMIN_MANAGED_ROLES: string[] = [
    USER_ROLES.DEVICE_MANAGER,
    USER_ROLES.VENDOR_MANUFACTURER,
]

export const enum TemplatePermissionMode {
    READ = 'READ',
    WRITE = 'WRITE',
    USE = 'USE',
    READ_WRITE = 'READ_WRITE',
    NA = 'NA',
}
export const drawerWidth = 250

export const minGeoFenceRadius = 0
export const defaultGeoFenceRadius = 5000
export const maxGeoFenceRadius = 50000

export const MAX_FILE_SIZE = 8388608 //8 MB. This is set for facility monitoring buiding layout plan. This limit is enforced by ngnix server.

/**
 * Single debounce (ms) for the "search-as-you-type" option lookups behind every
 * listing column filter (Organization, Carrier/Receiver/Shipper/Product and any
 * other Entity column). Centralised so all filters query at the same cadence —
 * UBQ-5749 (previously Organization debounced at 300ms while Entity columns were
 * double-debounced at ~600ms). Kept below the table-refetch debounce so the
 * dropdown still feels responsive while typing.
 */
export const LISTING_FILTER_SEARCH_DEBOUNCE_MS = 650

/**
 * What a column's filter control edits (see getColumnFilterKind). Stored as
 * the `type` of a column-level filter so it describes the control.
 */
export const enum ColumnFilterKind {
    ORGANIZATION = 'organization',
    ENTITY_TEMPLATE = 'entityTemplate',
    ITEM_NUMBER = 'itemNumber',
    PROGRAM = 'program',
    ENTITY = 'entity',
    DATE = 'date',
    DATE_TIME = 'dateTime',
    LIST = 'list',
    TEXT = 'text',
}

export const enum AttributeType {
    ACCELERATION = 'acceleration',
    BOOLEAN = 'boolean',
    CURRENCY = 'currency',
    DATE = 'date',
    DATE_TIME = 'datetime',
    DECIMAL = 'decimal',
    DISTANCE = 'distance',
    ENTITY = 'entity',
    Entity_JSON = 'entity json',
    ENUM = 'enum',
    FILE = 'file',
    GEO_FENCING = 'geofence',
    GEO_POSITION = 'geopostion',
    GPS = 'gps',
    HUMIDITY = 'humidity',
    JSON = 'json',
    LIGHT = 'light',
    MULTI_SELECT = 'multiselect',
    NUMBER = 'number',
    ORIENTATION = 'orientation',
    PDFBASE64 = 'pdfbase64',
    PRESSURE = 'pressure',
    RADIO = 'radio',
    RELATIVE_HUMIDITY = 'relative humidity',
    RULES_FIELD_TRIGGER = 'rules_field_trigger',
    SELECT = 'list',
    SHOCK = 'shock',
    STRING = 'string',
    TEMPERATURE = 'temperature',
    TEXT = 'text',
    TEXT_AREA = 'text area',
    THRESHOLD = 'threshold',
    URL = 'url',
    USER = 'user',
    USER_GROUP = 'user group',
    VIBRATION = 'vibration',
    WEIGHT = 'weight',
    SEPARATOR = 'separator',
    RULESET = 'rule set',
    TIME = 'time',
    SEALINE = 'sealine',
    FACILITY = 'facility',
    EMAIL = 'email',
    DEVICE_RELATION = 'device relation', // attribute for device relation in shipemt template// for CCl we are giving user to selecte device in first step
    CUSTOM_ATTRIBUTE = 'custom attribute',
    COUNTRYISOCODE = 'country',
    CREATION_DATETIME = 'creation date',
    SENSOR_SNAPSHOT_READINGS = 'sensor snapshot readings',
    ORGANIZATION_SELECT = 'organization select',
    ORGANIZATION_MULTI_SELECT = 'organization multi select',
}

export const enum EntityInstanceTypes {
    NORMAL = 'normal',
    IN_TRANSIT = 'intransit',
}

export const enum AttributesMetadata {
    ARRAY = 'Array',
    CHARTABLE = 'Chartable',
    DISPLAYABLE = 'Displayable',
    HIDDEN = 'Hidden',
    MAPPABLE = 'Mappable',
    SEARCHABLE = 'Searchable',
    SUPERVIEWER = 'Superviewer',
    TIMESERIES = 'TimeSeries',
    VIEW_ONLY = 'View-Only',
    FILTER = 'Filter',
    SORTING = 'Sorting',
    UNIQUE = 'Unique',
    UNIQUE_GLOBAL = 'Unique Global',
}

export const enum AttributeFunctionalType {
    Static = 'Static',
    Dynamic = 'Dynamic',
    Both = 'Both',
}

export const Template_Name = 'Entity Name' //for getting template name in entity table rows

export const localStorageVersionConstants = {
    facilityMonitoringVersion: 1,
}

export const enum RelationsType {
    ONE_TO_ONE = 'one to one',
    ONE_TO_MANY = 'one to many',
    MANY_TO_MANY = 'many to many',
    MANY_TO_ONE = 'many to one',
}

export const enum EntityStepTypes {
    Start_step = 'start_step',
    Attributes = 'attributes',
    Relations = 'relations',
}

export const BUSINESS_RULES_FIELD_ID = 'business_rules_rield_id'

export const enum PREVIEW_MODE {
    VIEW_ENTITY_DETAILS = 'VIEW_ENTITY_DETAILS',
    VIEW_ANALYTICS_DASHBOARD = 'VIEW_ANALYTICS_DASHBOARD',
}

export type IPREVIEW_MODE = typeof PREVIEW_MODE

export const enum TemplateProperties {
    ABSTRACT = 'Abstract',
    HIDDEN = 'Hidden',
    ENTITY_JSON = 'EntityJSON',
    RULESET = 'RuleSet',
    PREVIEW = 'Preview',
}

export const enum BusinessRulesAttributes {
    SCRIPT = 'script',
    TARGET = 'targetattribute',
    TARGETDISPLAYNAME = 'targetattributedisplayname',
    LOW_CRITICAL = 'low critical',
    LOW_CRITICAL_THRESHOLD = 'lowthreshold',
    HIGH_CRITICAL_THRESHOLD = 'highthreshold',
    LOW_WARNING_THRESHOLD = 'lowwarningthreshold',
    HIGH_WARNING_THRESHOLD = 'highwarningthreshold',
    LOW_INFO_THRESHOLD = 'lowinfothreshold',
    HIGH_INFO_THRESHOLD = 'highinfothreshold',
    TRIGGER_METHOD = 'triggermethod',
    AGGREGATE_LOW_HIGH = 'aggregatelowhigh',
    CRITICAL_DURATION = 'criticalduration',
    HIGH_CRITICAL_DURATION = 'highcriticalduration',
    LOW_CRITICAL_DURATION = 'lowcriticalduration',
    WARNING_DURATION = 'warningduration',
    LOW_WARNING_DURATION = 'lowwarningduration',
    HIGH_WARNING_DURATION = 'highwarningduration',
    INFO_DURATION = 'infoduration',
    HIGH_INFO_DURATION = 'highinfoduration',
    LOW_INFO_DURATION = 'lowinfoduration',
    DURATION = 'duration',
    POINTS = 'points',
    ACTIVE_STATUSES = 'activestatuses',
    // Open Road Dwell rule only. The toggle raises severity on a container
    // breach, and reads one of the two thresholds below to detect it — so
    // turning it on without either one configured does nothing (UBQ-6001).
    SECURITY_FUSION_ENABLED = 'securityfusionenabled',
    LIGHT_BREACH_THRESHOLD = 'lightbreachthreshold',
    SHOCK_THRESHOLD = 'shockthreshold',
}

export const BusinessRulesAttributesDurationLabels: Record<string, string> = {
    [BusinessRulesAttributes.CRITICAL_DURATION]:
        'Aggregate Critical Duration(Low + High)',
    [BusinessRulesAttributes.HIGH_CRITICAL_DURATION]: 'High Critical Duration',
    [BusinessRulesAttributes.LOW_CRITICAL_DURATION]: 'Low Critical Duration',
    [BusinessRulesAttributes.WARNING_DURATION]:
        'Aggregate Warning Duration(Low + High)',
    [BusinessRulesAttributes.LOW_WARNING_DURATION]: 'Low Warning Duration',
    [BusinessRulesAttributes.HIGH_WARNING_DURATION]: 'High Warning Duration',
    [BusinessRulesAttributes.INFO_DURATION]:
        'Aggregate Info Duration(Low + High)',
    [BusinessRulesAttributes.HIGH_INFO_DURATION]: 'High Info Duration',
    [BusinessRulesAttributes.LOW_INFO_DURATION]: 'Low Info Duration',
}

export const BusinessRulesAttributesPointsLabels: Record<string, string> = {
    [BusinessRulesAttributes.CRITICAL_DURATION]:
        'Aggregate Critical Points(Low + High)',
    [BusinessRulesAttributes.HIGH_CRITICAL_DURATION]: 'High Critical Points',
    [BusinessRulesAttributes.LOW_CRITICAL_DURATION]: 'Low Critical Points',
    [BusinessRulesAttributes.WARNING_DURATION]:
        'Aggregate Warning Points(Low + High)',
    [BusinessRulesAttributes.LOW_WARNING_DURATION]: 'Low Warning Points',
    [BusinessRulesAttributes.HIGH_WARNING_DURATION]: 'High Warning Points',
    [BusinessRulesAttributes.INFO_DURATION]:
        'Aggregate Info Points(Low + High)',
    [BusinessRulesAttributes.HIGH_INFO_DURATION]: 'High Info Points',
    [BusinessRulesAttributes.LOW_INFO_DURATION]: 'Low Info Points',
}

export const enum EntityStatus {
    ACTIVE = 'ACTIVE',
    DRAFT = 'DRAFT',
    ARCHIVE = 'ARCHIVE',
    DELETE = 'DELETED',
}

export const EntityStatusBEtoUI = {
    [EntityStatus.ACTIVE]: 'Active',
    [EntityStatus.ARCHIVE]: 'Completed',
}

// Virtual status for Device Manager — derived from active relation presence, not stored in DB
export const DEVICE_MANAGER_REGISTERED = 'REGISTERED'

export const DeviceManagerStatusToUI: Record<string, string> = {
    [DEVICE_MANAGER_REGISTERED]: 'Registered',
    [EntityStatus.ACTIVE]: 'Active',
    [EntityStatus.ARCHIVE]: 'Completed',
}

export const enum UserStatus {
    ACTIVE = 'active',
}

/********Facility Monitory Constants****************/

export const enum FMTABS {
    DASHBOARD = 0,
    MAP_VIEW = 1,
    SITE_VIEW = 2,
    ASSET_VIEW = 3,
    DEVICE_VIEW = 4,
    REPORTS = 5,
    RULES = 6,
    SETTINGS = 7,
}

export const enum TAB_PAGE_VIEW {
    LIST_VIEW = 'LIST_VIEW',
    DETAILS_VIEW = 'DETAILS_VIEW',
}

export const sensorCircleWidth = 25 //px
export const sensorCircleHeight = 25 //px
export const widthOfDragIndicator = 24 //px

export const floorLayoutDropTargets = {
    ASSET: 'ASSET',
    ASSET_SENSOR_MARKER: 'ASSET_SENSOR_MARKER',
}

/********InTransit Constants****************/

export const enum IN_TRANSIT_TABS {
    DASHBOARD = 0,
    MAP_VIEW = 1,
    RULES = 2,
    ALERTS = 3,
    SETTINGS = 4,
}

// localStorage key bases for In-Transit map view preferences.
// mapLayout is scoped per org + user (see buildScopedKey); mapSplitSizes is a
// single shared preference across all orgs/users on the browser.
export const INTRANSIT_STORAGE_KEYS = {
    mapLayout: 'intransit-map-layout',
    mapSplitSizes: 'intransit-map-split-sizes',
}

export type MapLayoutMode = 'both' | 'table-only'
export const VALID_MAP_LAYOUTS: MapLayoutMode[] = ['both', 'table-only']

/************Device Manager*************** */

export const enum DEVICE_MANAGER_TABS {
    DASHBOARD = 0,
    LIST_VIEW = 1,
    SETTINGS = 2,
}
/**
 * UBQ-5487: `ubq.alert_status` values. The stored numbers are unchanged — only what each one means.
 *
 * UBQ-5337 surfaced 2 (seeded "ignore") as Acknowledge and left 1 as a passive Read flag, which read
 * backwards to users. So 1 is now **Acknowledge** and 2 is **Ignore**. Renaming rather than migrating
 * is deliberate: every alert already at 1 becomes Acknowledged with no data change.
 *
 * Both are deliberate "a human has attended to this" states and both earn the map checkmark, per
 * UBQ-5337's AC "every ack-requiring alert is Acknowledged or Ignored". Only UNREAD blocks it.
 * There is no passive state left — all three are user choices.
 */
export const ALERT_STATUS = {
    UNREAD: 0,
    ACKNOWLEDGED: 1,
    IGNORED: 2,
} as const

/** Severities that require acknowledgment before a pin earns the checkmark. */
export const ACK_REQUIRING_SEVERITIES = [
    'Low Critical',
    'High Critical',
    'Critical',
]

/**
 * In-alarm pin whose every ack-requiring alert has been acted on (Acknowledged or Ignored).
 *
 * The artwork is the one shipped under UBQ-4063 and is unchanged — red pin, white ring, white
 * checkmark knocked out of a solid red disc, verified against that ticket's QA screenshot (attachment
 * 32379). Do not swap it for a lighter overlay; the solid disc is what reads at 24px. UBQ-5487 renamed
 * the file from `red-marker-read.svg`, since "read" is no longer a state.
 *
 * Known deviation: UBQ-5337's icon AC asks for a *blue* checkmark. Matching the already-shipped
 * UBQ-4063 icon was chosen instead; flag at the PO/Ali icon review.
 */
export const redMarkerIconAcknowledged =
    '/assets/images/map/red-marker-acknowledged.svg'
export const redMarkerIconUnread = '/assets/images/map/red-marker-unread.svg'
export const redMarkerIcon =
    'https://maps.google.com/mapfiles/ms/icons/red-dot.png'
export const greenMarkerIcon = 'assets/images/map/green-marker-unread.svg'

export const greenClusterIcon = globalThis.btoa(`
    <svg fill="green" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
      <circle cx="120" cy="120" opacity=".6" r="70" />
      <circle cx="120" cy="120" opacity=".3" r="90" />
      <circle cx="120" cy="120" opacity=".2" r="110" />
      <circle cx="120" cy="120" opacity=".1" r="130" />
    </svg>`)

export const redClusterIcon = globalThis.btoa(`
    <svg fill="red" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
      <circle cx="120" cy="120" opacity=".6" r="70" />
      <circle cx="120" cy="120" opacity=".3" r="90" />
      <circle cx="120" cy="120" opacity=".2" r="110" />
      <circle cx="120" cy="120" opacity=".1" r="130" />
    </svg>`)

export const googleMapOptions: google.maps.MapOptions = {
    scrollwheel: true,
    streetViewControl: false,
    cameraControl: false,
    zoomControl: true,
    mapTypeControl: false,
    clickableIcons: false, // hides the native landmark tooltip and its "View on Google Maps" link
}

export const enum ShipmentAttributes {
    OrganizationName = 'Organization Name',
    TripNumber = 'Trip Number',
    ShipmentNameAttributeId = 'shipmentName',
    PurchaseOrderAttributeId = 'purchaseOrder',
    ContainerNumberAttributeId = 'containerNumber',
    VesselsAttributeId = 'vessels',
    AlarmStatusAttributeId = 'alarmStatus',
    OriginLocationAttributeId = 'originLocation',
    CarrierAttributeId = 'carrier',
    SealineAttributeId = 'sealine',
    DestinationLocationAttributeId = 'destinationLocation',
    CurrentLocationAttributeId = 'currentLocation',
    LocationAttributeId = 'location',
    ProductAttributeId = 'product',
    FirstReadingAttributeId = 'firstReading',
    ActualDepartureDateAttributeId = 'actualDepartureDate',
    ActualArrivalDateAttributeId = 'actualArrivalDate',
    LastKnownAddressAttributeId = 'lastKnownAddress',
    LastReadingAttributeId = 'lastReading',
    ShipmentStatusAttributeId = 'shipmentStatus',
    BatteryPercentage = 'batteryPercent',
    CargoInsuranceAttributeId = 'cargoInsurance',
    ShareListAttributeId = 'shareList',
}

export const enum InsuranceAttributes {
    SystemPolicyNumber = 'systemPolicyNumber',
    PrimaryAssuredCompanyCode = 'primaryAssuredCompanyCode',
    Origin = 'origin',
    Destination = 'destination',
    Shipment = 'shipment',
    InsuranceStatus = 'insuranceStatus',
    CountryISOcode = 'countryIso',
    QuoteExpiryDate = 'quoteExpiryDate',
    ConveyanceClientCode = 'conveyanceClientCode',
    Loading = 'loading',
    Discharge = 'discharge',
    InsuredValue = 'insuredValue',
}
export const enum InsuranceStatus {
    ACCEPT = 'Quote Approved',
    REJECT = 'Quote Rejected',
    ISSUED = 'Quote Issued',
}
export const enum ShipmentStatus {
    AT_ORIGIN = 'at origin',
    IN_TRANSIT = 'in transit',
}

/********Analytics Constants****************/

export const enum ANALYTICS_TABS {
    LIST_VIEW = 0,
    SETTINGS = 1,
}

/*** FlashTrak Constants *****/

export const enum FLASHTRAK_TABS {
    IFRAME_VIEW = 0,
    SETTINGS_VIEW = 1,
}

/********Application Names Constants****************/

export const Applications = {
    facilityMonitoring: {
        id: 'facilityMonitoring',
        label: 'facilityMonitoring',
    },
    inTransit: {
        id: 'inTransit',
        label: 'coldChainLogistics',
    },
    analytics: {
        id: 'analytics',
        label: 'analytics',
    },
    flashtrak: {
        id: 'flashtrak',
        label: 'flashtrak',
    },
    deviceManager: {
        id: 'deviceManager',
        label: 'Device Manager',
    },
    carbonEmission: {
        id: 'Carbon Emission',
        label: 'Carbon Emission',
    },
    cargoInsurance: {
        id: 'Cargo Insurance',
        label: 'Cargo Insurance',
    },
    maritimeTracking: {
        id: 'Maritime Tracking',
        label: 'Maritime Tracking',
    },
    uploadManager: {
        id: 'uploadManager',
        label: 'uploadManager',
    },
}

/** Route Constants */
export const RoutePaths = {
    guestUserShipment: '/guestUser/shipment',
    facilityMonitoring: '/facilityMonitoring',
    inTransit: '/inTransit',
    ccl: '/coldChainLogistics',
    analytics: '/analytics',
    flashtrak: '/flashtrak',
    deviceManager: '/deviceManager',
    uploadManager: '/uploadManager',
    entity: '/entity',
    networkAdmin: '/networkAdmin',
    dtStudio: '/dtStudio',
    unverifiedUser: '/unverifiedUser',
    programs: '/programs',
    organizations: '/organizations',
    userManagement: '/user-mgmt',
    userGroups: '/user-groups',
    userGroup: '/user-group',
    Home: '/',
    entityPreview: '/entity/preview',
    analyticsPreview: '/analytics/preview',
    networkUsers: '/setup',
}
export const periodOptions = [
    'Last 2 hrs',
    'Last 4 hrs',
    'Last 8 hrs',
    'Last Day',
    'Last Week',
    'Last Month',
    'Custom',
]

/**
 * Below locationMarkerSVG is similar to the path in "public/assets/images/icons/location-icon.svg"
 */
export const locationMarkerSVG: string =
    'M20.62 8.45C19.57 3.83 15.54 1.75 12 1.75C12 1.75 12 1.75 11.99 1.75C8.46001 1.75 4.42001 3.82 3.37001 8.44C2.20001 13.6 7.43999 19.44 8.22001 20.72C9.00004 22 12 25.5 12 25.5C12 25.5 14.5 22.5 15.77 20.72C17.04 18.94 21.79 13.61 20.62 8.45ZM12 13.46C10.26 13.46 8.85001 12.05 8.85001 10.31C8.85001 8.57 10.26 7.16 12 7.16C13.74 7.16 15.15 8.57 15.15 10.31C15.15 12.05 13.74 13.46 12 13.46Z' // Default tear drop shape

/**
 * Mandatory Fields in InTransit Settings
 */
export const mandatoryInTransitSettingsKeys: InTransitSettingsKeys[] = [
    'analyticsDashboardId',
    'shipmentEntityTemplateId',
    'locationAttributeId',
    'shipmentIdAttributeId',
    'shipmentNameAttributeId',
    'shipmentStatusAttributeId',
    'wayPointsAttributeId',
    'actualDepartureDateAttributeId',
    'actualArrivalDateAttributeId',
    'originAttributeId',
    'destinationAttributeId',
    'reportIdAttributeId',
    // 'insuranceEntityTemplateId',
    // 'insuredValueAttributeId',
    // 'quotePremiumAmountAttributeId',
    // 'legalAssuredNameAttributeId',
    // 'insuranceStatusAttributeId',
    // 'certificateUrlAttributeId',
    // 'searatesEntityTemplateId',
    // 'AISLocationAttributeId',
    // 'pinLocationAttributeId',
    // 'locationsGeoJSONAttributeId',
    // 'pathGeoJSONAttributeId',
    'deviceEntityTemplateId',
    'deviceLocationAttributeId',
    // 'locationsAttributeId',
    // 'routeAttributeId',
    // 'vesselsAttributeId',
    // 'containersAttributeId',
    // 'carbonEmissionsEntityTemplateId',
    // 'totalEmissionsAttributeId',
    // 'reportFileAttributeId',
    // 'emissionsAttriuteId',
    'reportsEntityTemplateId',
    'reportDataFromAttributeId',
    'reportDataUntilAttributeId',
    'reportingObjectAttributeId',
    'reportURLAttributeId',
]

export const inTransitEntitiesTree: IEntities = {
    shipmentEntityTemplateId: {
        attributes: [
            'locationAttributeId',
            'shipmentIdAttributeId',
            'shipmentNameAttributeId',
            'shipmentStatusAttributeId',
            'alarmStatusAttributeId',
            'wayPointsAttributeId',
            'originAttributeId',
            'destinationAttributeId',
            'reportIdAttributeId',
        ],

        relations: {
            searatesEntityTemplateId: {
                attributes: [
                    'AISLocationAttributeId',
                    'pinLocationAttributeId',
                    'locationsGeoJSONAttributeId',
                    'pathGeoJSONAttributeId',
                    'containersAttributeId',
                    'vesselsAttributeId',
                    'locationsAttributeId',
                    'routeAttributeId',
                ],
                relations: null,
            },
            deviceEntityTemplateId: {
                attributes: ['deviceLocationAttributeId'],
                relations: null,
            },
            carbonEmissionsEntityTemplateId: {
                attributes: [
                    'totalEmissionsAttributeId',
                    'reportFileAttributeId',
                ],
                relations: null,
            },
        },
    },
    reportsEntityTemplateId: {
        attributes: [
            'reportDataFromAttributeId',
            'reportDataUntilAttributeId',
            'reportingObjectAttributeId',
            'reportURLAttributeId',
        ],
        relations: null,
    },
}

export const widgetView = {
    MAP_VIEW: 'MAP_VIEW',
    GRAPH_APACHE_ECHART_VIEW: 'GRAPH_APACHE_ECHART_VIEW',
    MARINE_REPORT_VIEW: 'MARINE_REPORT_VIEW',
    CARBON_REPORT_VIEW: 'CARBON_REPORT_VIEW',
    INSURANCE_VIEW: 'INSURANCE_VIEW',
    GENERAL_REPORT_VIEW: 'GENERAL_REPORT_VIEW', // This is probably asset report view
    WORKFLOW_VIEW: 'WORKFLOW_VIEW',
    ALERTS_VIEW: 'ALERTS_VIEW',
    ANALYSIS_VIEW: 'ANALYSIS_VIEW',
}

export type thresholdTypes =
    | 'highCritical'
    | 'highWarning'
    | 'lowCritical'
    | 'lowWarning'
    | 'lowThreshold'
    | 'highThreshold'

export type IThresholds = {
    [key in thresholdTypes]: {
        label: string
        color: string
        displayOnGraph: boolean
    }
}

export const thresholds: IThresholds = {
    highCritical: {
        label: 'High Critical',
        color: 'red',
        displayOnGraph: true,
    },
    highWarning: {
        label: 'High Warning',
        color: 'orange',
        displayOnGraph: false,
    },
    lowCritical: {
        label: 'Low Critical',
        color: 'red',
        displayOnGraph: true,
    },
    lowWarning: {
        label: 'Low Warning',
        color: 'orange',
        displayOnGraph: false,
    },
    highThreshold: {
        label: 'High Threshold',
        color: 'red',
        displayOnGraph: true,
    },
    lowThreshold: {
        label: 'Low Threshold',
        color: 'red',
        displayOnGraph: true,
    },
}

/**
 * Should we get these actions from API so that we FE and BE will be sync always
 */
export const actions = {
    available_actions: {
        COMPLETE: 'Complete',
    },
    // completed_actions: ?,
    // pending_actions: ?,
}

export const relationStatus = {
    ACTIVE: 'ACTIVE',
    TERMINATE: 'TERMINATE',
}

export const publicShipmentView = {
    SHIPMENT_SEACH: 'SHIPMENT_SEARCH',
    SHIPMENT_PREVIEW: 'SHIPMENT_PREVIEW',
}

export const URLSearchParamsKeys = {
    dynamicLevelPrefix: 'dynamicLevel_',
    selectedSiteUID: 'selectedSiteUID',
    selectedSiteName: 'selectedSiteName',
    selectedBuildingUID: 'selectedBuildingUID',
    instanceId: 'instanceId',
    templateId: 'templateId',
    pageView: 'pageView', //This represents component(Entity or ViewEntity) that is loaded as a page under any tabIndex(tabView) of an application
    tabIndex: 'tabIndex',
    applicationId: 'applicationId',
    orgId: 'orgId',
    isGuestUser: 'isGuestUser',
    tripNumber: 'tripNumber',
    publicShipmentView: 'publicShipmentView',
}

export const muiTabsHeight = '36px'

interface ColumnExclusion {
    [applicationId: string]: {
        [tabIndex: number]: string[]
    }
}

/** UBQ-5531: the Item Number Custom Attribute key, and the relation its options come from. */
export const ITEM_NUMBER_ATTRIBUTE_NAME = 'itemNumber'
export const ITEM_NUMBER_RELATION_NAME = 'device'

/**
 * UBQ-5628: id of the shared, abstract root "Device" template. It is a seeded
 * master template owned by the Root org and shared (not cloned) to every org,
 * so this id is identical across orgs and environments. Every device sub-template
 * carries it in its `treePath`, which is how we recognise a Device creation
 * regardless of depth or the org's Device Manager settings.
 */
export const DEVICE_ABSTRACT_TEMPLATE_ID =
    '88ca9860-9dfd-4b43-bbc8-3c491953e16e'

export const columsnToExcludeInEntityListing: ColumnExclusion = {
    [Applications.inTransit.id]: {
        [IN_TRANSIT_TABS.MAP_VIEW]: ['Id'],
    },
    [Applications.facilityMonitoring.id]: {
        [FMTABS.ASSET_VIEW]: [],
        [FMTABS.REPORTS]: ['Type', 'Organization Name'],
        [FMTABS.DEVICE_VIEW]: [],
    },
}

export const USACenter = { lat: 39.8283, lng: -98.5795 }

export const ReportTypes = [
    { id: 't1andl1', name: 't1AndL1Only' },
    { id: 'devicemodel', name: 'allSupportedSensors' },
] as const
export const defaultFormData: IOrgformData = {
    parentOrganizationId: '',
    name: '',
    description: '',
    applicationAccess: [],
    preferences: {
        token: '',
        isSync: false,
        handGrenade: true,
        deviceAutoPowerOff: true,
        reportType: ReportTypes[0].id,
        fixTemperatureLocationDrift: false,
        // UBQ-5678: the PDF route map is opt-in per organization.
        routeMapInPdfReport: false,
    },
    orgUnits: {},
    accountId: '',
    localization: defaultLocalization,
    insuranceDetails: [],
    defaultProduct: null,
    defaultShipper: null,
    defaultReceiver: null,
}
export const defaultAddUserFormData: IformData = {
    role: '',
    email: '',
    units: {},
}

export enum MODE {
    ADD = 'ADD',
    EDIT = 'EDIT',
}

export const MapTypes = {
    circle: 'Circle',
    polygon: 'Polygon',
} as const

export const ruleAttributesToExclude = [
    'ruleId',
    'ruleTemplateId',
    'ruleTemplateName',
    'Script',
    'License Duration',
    'Rule Name',
]
export const locationChangeType = {
    INPUT_EVENT: 'INPUT_EVENT',
    OPTION_SELECTED: 'OPTION_SELECTED',
}

export const defaultTransportWeight = {
    kg: 19000,
    lbs: 41887.8,
    t: 19,
    oz: 670208,
}

export const temperatureUnits: string[] = ['°C', '°F', 'K']

export const RootOrgName = 'Root'

export const ColumnFilterType = {
    Upload_Manager_type: 'upload manager type',
}

export const RELATION_TYPE_PARENT = 'Parent'
export const RELATION_TYPE_CHILD = 'Child'
export const SEARCH_ATTRIBUTE_TYPE_PARENT_RELATION = 'parentRelation'
